import { FOCUS_RING, cn, useLanguage, useTranslate } from '@athena/ui'
import { useNavigate, useRouterState } from '@tanstack/react-router'

import { withLanguage, type Language } from '../../lib/language.js'

/**
 * The other language, on the screens a person sees before they are inside
 * Athena: sign-in, an invitation, onboarding.
 *
 * The public site's switch, deliberately — the same two-letter code in the
 * same small bordered box at the right of the header — so the control a
 * visitor just used on `/es` is the one they find here. Inside the product the
 * choice lives in the profile menu instead; there is no profile yet.
 *
 * A real link to the same screen under the other prefix, query string kept
 * (a `?redirect=` survives), so it works as a link — middle-click, copy — and
 * the router moves between the two without a reload, which keeps the page
 * where it is. Nothing is stored: the visitor is not signed in, and the URL is
 * what decides a page's language.
 */
export function AuthLanguageSwitch() {
  const t = useTranslate()
  const language = useLanguage()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const searchStr = useRouterState({ select: (state) => state.location.searchStr })
  const other: Language = language === 'es' ? 'en' : 'es'
  const href = `${withLanguage(pathname, other)}${searchStr}`

  return (
    <a
      href={href}
      lang={other}
      hrefLang={other}
      aria-label={t(`language.${other}`)}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        void navigate({ href, replace: true })
      }}
      className={cn(
        'inline-flex h-control-sm min-w-11 items-center justify-center rounded-sm border border-border px-2',
        'text-label font-semibold text-text hover:border-border-strong hover:text-primary',
        'max-md:h-touch-target-min max-md:min-w-touch-target-min',
        FOCUS_RING,
      )}
    >
      {other.toUpperCase()}
    </a>
  )
}
